import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UsersResults } from '@common/data-models/search-results/users-result';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { UserStaffInfo } from '@common/data-models/service/user-staff-info';
import { GlobalRoles, InvitationStatusCodes } from '@common/utils/util';

import { GlobalTablesService } from './global-tables-service';
import { NomWebServicesJson } from './nom-web-services-json';

@Injectable({ providedIn: 'root' })
export class TeamMembersService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService
    ) {}

    public async getTeamMembers(team?: string, role?: string, status?: number, searchTerm?: any): Promise<ServiceResponse<{ userResults: UsersResults; security: Array<any> }>> {
        const userResults = new UsersResults();
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.get('/api/TeamMembers', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (err) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load users!',
                detailMessage: `Failed to load users! \n\rStackTrace: NomWebServicesJson.getTeamMembers(); \n\rError: ${err}`,
            });
            return new ServiceResponse<{ userResults: UsersResults; security: Array<any> }>(true, { userResults: new UsersResults(), security: [] });
        }
        const res = response.body;
        if (team || role || status !== -1) {
            res.TeamMembers = res.TeamMembers.filter(user => {
                let teamRes = true;
                let roleRes = true;
                let statusRes = true;
                if (team) {
                    teamRes = user.Program === team;
                }
                if (role) {
                    roleRes = role !== GlobalRoles.SUPERVISOR_ID ? user.GlobalRole === role : user.Supervisor;
                }
                if (status !== -1) {
                    statusRes = user.Active !== status;
                }
                return teamRes && roleRes && statusRes;
            });
        } else {
            res.TeamMembers;
        }

        if (searchTerm) {
            res.TeamMembers = res.TeamMembers.filter(
                user => user.GivenName.toString().toLowerCase() === searchTerm.toLowerCase() || user.Surname.toString().toLowerCase() === searchTerm.toLowerCase()
            );
        } else {
            res.TeamMembers;
        }

        for (const p of res.TeamMembers) {
            const user = Object.assign(new UserStaffInfo(), p);
            if (!user.InvitationStatus) {
                user.InvitationStatus = InvitationStatusCodes.NEW;
            }
            userResults.addUser(user);
        }

        // Store Security table in Globals
        this.globalTablesService.populateSecurityRoles(res.Security);

        const ret = {
            userResults,
            security: res.Security,
        };
        return new ServiceResponse<{ userResults: UsersResults; security: Array<any> }>(true, ret);
    }

    public async updateTeamMember(updateMember: UserStaffInfo): Promise<ServiceResponse<boolean>> {
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.patch('/api/TeamMembers', updateMember, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (err) {
            this.nomWebServicesJson.showLockScreenError(err);
            return new ServiceResponse<boolean>(false, null);
        }
        const res = response.body;
        return new ServiceResponse<boolean>(true, res);
    }

    public async createTeamMember(updateMember: UserStaffInfo): Promise<ServiceResponse<boolean>> {
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.put('/api/TeamMembers', updateMember, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (err) {
            this.nomWebServicesJson.showLockScreenError(err);
            return new ServiceResponse<boolean>(false, null);
        }
        const res = response.body;
        return new ServiceResponse<boolean>(true, res);
    }

    public async inviteTeamMember(userId: string, email: string): Promise<ServiceResponse<boolean>> {
        try {
            const replyUrl = window.location.href.split('#')[0];
            await this.nomWebServicesJson.post(
                '/api/Invitation',
                {
                    userId,
                    email,
                    replyUrl: `${replyUrl}#/login`,
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                    }),
                    responseType: 'json',
                    observe: 'response',
                }
            );
        } catch (err) {
            this.nomWebServicesJson.showLockScreenError(err);
            return new ServiceResponse<boolean>(false, null);
        }
        return new ServiceResponse<boolean>(true, '');
    }

    public async removeTeamMember(userID: string): Promise<ServiceResponse<boolean>> {
        try {
            await this.nomWebServicesJson.delete('/api/TeamMembers', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    userID,
                },
                observe: 'response',
            });
        } catch (err) {
            this.nomWebServicesJson.showLockScreenError(err);
            return new ServiceResponse<boolean>(false, false);
        }
        return new ServiceResponse<boolean>(true, true);
    }
}
